import { useProgramContacts } from '../../../hooks/useProgramContacts';
import { ContactsForm } from './ContactsForm';
import { QcFlagCard } from './QcFlagCard';
import { RunUpdateButton } from './RunUpdateButton';
import styles from './ContactsPanel.module.css';

export interface ContactsPanelProps {
  programId: string;
  onBack?: () => void;
}

export function ContactsPanel({ programId, onBack }: ContactsPanelProps) {
  const { data, isLoading, error, refetch } = useProgramContacts(programId);

  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.loading}>Loading program contacts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.panel}>
        <div className={styles.error}>
          Error loading program contacts: {error.message}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.panel}>
        <div className={styles.emptyState}>
          <h2 className={styles.emptyStateTitle}>No Data Found</h2>
          <p className={styles.emptyStateText}>
            Program not found or has no contact information.
          </p>
        </div>
      </div>
    );
  }

  const derivedSeedUrl = (() => {
    const rawSeedUrls = data.seed_url ? data.seed_url.split(',') : [];
    const firstSeedUrl = rawSeedUrls
      .map((url) => url.trim())
      .find((url) => url.length > 0);

    return firstSeedUrl || data.website_url || '';
  })();

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {onBack && (
            <a href="#data-quality" className={styles.backLink} onClick={onBack}>
              ← Back
            </a>
          )}
          <div>
            <h1 className={styles.title}>Quality Check: {data.program_name}</h1>
            <p className={styles.subtitle}>{data.baci_code}</p>
          </div>
        </div>
      </div>

      {/* Two-column grid */}
      <div className={styles.grid}>
        {/* Left column: Editable contact form */}
        <div className={styles.column}>
          <ContactsForm programId={programId} contactsData={data} />
        </div>

        {/* Right column: QC status and flags */}
        <div className={styles.column}>
          <QcFlagCard
            programId={programId}
            qcFlag={data.qc_flag_contacts}
            qcNotes={data.qc_flag_contacts_notes}
          />
        </div>
      </div>

      {/* Action bar */}
      <div className={styles.actions}>
        <RunUpdateButton
          programId={programId}
          baciCode={data.baci_code}
          seedUrl={derivedSeedUrl}
          organization={data.awarding_organization ?? undefined}
          onComplete={() => refetch()}
        />
      </div>
    </div>
  );
}

export default ContactsPanel;
